<template>
  <svg xmlns="http://www.w3.org/2000/svg"
    xmlns:xlink="http://www.w3.org/1999/xlink"
    :viewBox="svgViewbox"
    :width="viewbox?.width"
    :height="viewbox?.height"
    @mousedown="onDragStart"
    @mouseup="onDragEnd"
    @mousemove="onDragMove"
    @wheel="onZoom"
  >
    <Wmts v-model:viewbox="viewbox" v-model:layers="layers" v-model:layer="layer" v-model:zooms="zooms" v-model:zoom="zoom" />
    <slot />
</svg>
</template>

<script setup>
  import Wmts from "./Wmts.vue";
  import { logger } from "../../services/logger";
  import { ref, computed, onMounted, watch, provide } from "vue";

  const viewbox = ref({x: 0, y: 0, width: 100, height: 100});
  const currentCentroid = ref({x: 0, y: 0});
  const targetCentroid = defineModel("centroid");
  const layers = defineModel("layers");
  const layer = defineModel("layer");
  const zooms = defineModel("zooms");
  const zoom = defineModel("zoom");

  provide("mapZooms", zooms);
  provide("mapZoom", zoom);

  const emit = defineEmits("mouse:down", "mouse:up");

  const svgViewbox = computed(() => {
    viewbox.value = getViewbox(currentCentroid.value, zoom.value);
    if (viewbox.value == null) return "0 0 100 100";
    const svgViewbox = [
      viewbox.value.x,
      -viewbox.value.y - viewbox.value.height,
      viewbox.value.width,
      viewbox.value.height,
    ].join(" ");
    logger.debug("Map.vue: svgViewbox", svgViewbox);
    return svgViewbox;
  });

  watch(()=> targetCentroid.value, () => {
    if (targetCentroid.value) {
      animateMoveTo({
        x: targetCentroid.value.x,
        y: targetCentroid.value.y
      });
    }
  });

  watch(() => currentCentroid.value, () =>
  {
    viewbox.value = getViewbox(currentCentroid.value, zoom.value);
  });
  window.addEventListener("resize", () => {
    viewbox.value = getViewbox(currentCentroid.value, zoom.value);
  });


  function animateMoveTo(newCentroid, duration = 300) {
    if (!newCentroid) return;
    const startCentroid = { ...currentCentroid.value };
    const deltaX = newCentroid.x - startCentroid.x;
    const deltaY = newCentroid.y - startCentroid.y;
    const startTime = performance.now();

    function animate(time) {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      currentCentroid.value = {
        x: startCentroid.x + deltaX * progress,
        y: startCentroid.y + deltaY * progress
      };

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    }

    requestAnimationFrame(animate);
  }

  function getViewbox(centroid, zoomLevel) {
    if (!centroid || !zoomLevel) return null;
    const zoomDetails = getZoomDetails(zoomLevel);
    const widthPerPixel = zoomDetails?.width_per_pixel ?? 0;
    const heightPerPixel = zoomDetails?.height_per_pixel ?? 0;
    const width = window.innerWidth * widthPerPixel;
    const height = window.innerHeight * heightPerPixel;
    const x = centroid.x - (width / 2.0);
    const y = centroid.y - (height / 2.0);
    const newViewbox = {
      x: x,
      y: y,
      width: width,
      height: height,
      widthMeterPerPixel: widthPerPixel,
      heightMeterPerPixel: heightPerPixel,
      centroid: centroid
    };
    return newViewbox;
  }

  function getSvgCoordinates(x, y) {
    if (!viewbox.value) return {x: 0, y: 0};
    const svgX = viewbox.value.x + x * viewbox.value.widthMeterPerPixel;
    const svgY = (viewbox.value.y + viewbox.value.height) - y * viewbox.value.heightMeterPerPixel;
    return { x: svgX, y: svgY };
  }

  let dragging = false;
  function onDragStart(event) {
    event.preventDefault();
    dragging = true;
    const coordinates = getSvgCoordinates(event.offsetX, event.offsetY);
    emit("mouse:down", coordinates, event);
  }

  function onDragEnd(event) {
    event.preventDefault();
    dragging = false;
    const coordinates = getSvgCoordinates(event.offsetX, event.offsetY);
    emit("mouse:up", coordinates, event);
  }

  function onDragMove(event) {
    event.preventDefault();
    if (!dragging) return;
    const widthPerPixel = viewbox.value.widthMeterPerPixel;
    const heightPerPixel = viewbox.value.heightMeterPerPixel;
    const xLocal = event.movementX * widthPerPixel;
    const yLocal = event.movementY * heightPerPixel;
    currentCentroid.value.x -= xLocal;
    currentCentroid.value.y += yLocal;
  }

  let zoomDebounceTimeout = null;
  function onZoom(event) {
    event.preventDefault();
    if (zoomDebounceTimeout != null) return;
    zoomDebounceTimeout = setTimeout(() => {
      zoomDebounceTimeout = null;
    }, 250);
    if (!viewbox.value || !zooms.value || !zoom.value) return;

    // Sort zoom levels: ascending zoom number = more zoomed out (larger width_per_pixel)
    const sortedZooms = [...zooms.value].sort((a, b) => a.zoom - b.zoom);
    const currentIndex = sortedZooms.findIndex(z => z.zoom === zoom.value);

    // deltaY < 0 = scroll up = zoom in (higher index), deltaY > 0 = zoom out (lower index)
    const newIndex = currentIndex + (event.deltaY < 0 ? 1 : -1);
    if (newIndex < 0 || newIndex >= sortedZooms.length) return;

    const newZoomEntry = sortedZooms[newIndex];
    const newWidth = window.innerWidth * newZoomEntry.width_per_pixel;
    const newHeight = window.innerHeight * newZoomEntry.height_per_pixel;

    // Screen-space ratio of mouse position (0..1)
    const svgEl = event.currentTarget;
    const ratioX = event.offsetX / svgEl.clientWidth;
    const ratioY = event.offsetY / svgEl.clientHeight;

    // World coordinates under the mouse cursor (Y-axis is flipped in SVG)
    const worldX = viewbox.value.x + ratioX * viewbox.value.width;
    const worldY = viewbox.value.y + (1 - ratioY) * viewbox.value.height;

    // Recalculate centroid so the world point stays under the cursor after zoom
    currentCentroid.value = {
      x: worldX + (0.5 - ratioX) * newWidth,
      y: worldY + (ratioY - 0.5) * newHeight
    };

    zoom.value = newZoomEntry.zoom;
  }

  function getZoomDetails(zoomLevel) {
    let zoomObj = zooms.value.find(z => z.zoom === zoomLevel);
    zoomObj ??= zooms.value.find(z => z.default);
    return zoomObj;
  }
</script>
